import ClassTeachingDetails from "@/components/ClassTeachingDetails";
/**
 * @fileoverview Compose a manual (typed, no-audio) lesson — Spec 10.
 *
 * Web parity with mobile's create-manual mode: the compose surface IS the
 * lesson document in edit mode — the same header, reading column, metadata rail
 * and EditableList rows as LessonSummary's editing state — rather than a
 * cramped modal form. Typing a lesson up should look like editing one.
 *
 * **Prep mode.** Opened with `?occurrence=<id>` for a class meeting more than
 * five minutes off, this is the same document writing a PLAN: one body field,
 * one title, one practice list, and only the destination and the words on the
 * button change. Saving writes a draft that is not a lesson — no teaching
 * evidence, no usage, nothing sent — and the text is waiting in this same field
 * when the session starts and the page becomes an ordinary write-up. A page left
 * open walks over that line by itself. See `lib/prepNotes`.
 *
 * Target comes from the query string: `?student=<id>` (POST /lessons/manual) or
 * `?class=<id>` (POST /classes/{id}/lessons/manual). Optional `?slot=&date=`
 * preselects a scheduled-occurrence stamp; the page also loads today's
 * occurrences for the quick-pick, with the same stamp discipline as recording
 * (the occurrence's identity date is what rides the request). For a `revoked`
 * student the page carries the lock-context line so the feature never reads as
 * a consent workaround (Spec 10 §5 #2).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import SessionBrief from "@/components/SessionBrief";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, Button, Eyebrow, Icon } from "@/components/studio/primitives";
import { EditableList, FIELD_CLS, blankItem, cleanList, listProps } from "@/components/studio/EditableList";
import RichTextEditor from "@/components/studio/RichTextEditor";
import { SCHEDULING_ENABLED, SCHEDULER_VIEW_ENABLED } from "@/lib/featureFlags";
import LessonWorkspaceStatus from "@/components/studio/LessonWorkspaceStatus";
import { occurrenceKey, selectionFromParams, selectionFromOccurrence, sameSelection,
  selectionPayload, teachingToday, verifySelection } from "@/lib/schedulingView";
import { sectionLabel } from "@/lib/lessonSections";
import RequestSubDialog from "@/components/studio/RequestSubDialog";
import { isPrepWindow } from "@/lib/prepNotes";
import { formatTime12h, weekdayName } from "@/lib/schedule";

/** Local YYYY-MM-DD for today (never UTC — the lesson date is instructor-local). */
const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Render a YYYY-MM-DD as "Jul 24, 2026" without tripping over UTC parsing. */
const formatDate = (ymd) => {
  const [y, m, d] = (ymd || "").split("-").map(Number);
  if (!y || !m || !d) return "";
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const ManualLessonCompose = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studentId = searchParams.get("student");
  const classId = searchParams.get("class");
  // The meeting this page was opened from, which is what a plan belongs to.
  // Absent for a lesson typed up from the roster.
  const occurrenceId = searchParams.get("occurrence");
  const isClass = !!classId;

  const [student, setStudent] = useState(null);
  const [cls, setCls] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduledToday, setScheduledToday] = useState([]);

  const [roster, setRoster] = useState([]);
  // The register: student id → "absent" | "excused", for the students the
  // instructor marked. Ids only — the names are resolved and snapshotted
  // server-side at save, so a later roster revision cannot rewrite who missed
  // an old session.
  const [marks, setMarks] = useState(() => new Map());
  const absentIds = [...marks].filter(([, m]) => m === "absent").map(([id]) => id);
  const excusedIds = [...marks].filter(([, m]) => m === "excused").map(([id]) => id);

  // Seeded from the deep link: tapping a class on the calendar has already
  // said which lesson this is. Still editable — a default, not a decision.
  const [titleText, setTitleText] = useState(() => searchParams.get("title") || "");
  const [summaryText, setSummaryText] = useState("");
  // A class starts with NO practice row. A private lesson is one student and
  // ends in something to practise; a class of nine usually does not, and the
  // blank numbered row asked every one of them to be answered or deleted.
  // "Add practice item" is still right there when the class does earn one.
  const [practiceItems, setPracticeItems] = useState(() => (classId ? [] : [blankItem()]));
  const [instructorNotes, setInstructorNotes] = useState("");
  // The sections a recorded lesson has, offered to a typed one too so it
  // reads, emails and exports the same way (iOS review B5). Both start empty:
  // "Add" is right there when the lesson earns one.
  const [studentFocus, setStudentFocus] = useState([]);
  const [coreConcepts, setCoreConcepts] = useState([]);
  // A legacy (slot, date) occurrence carries its own date and is never
  // re-verified, so seed from the link rather than defaulting to today — the
  // notes would otherwise be stamped to the session and dated to now.
  const [lessonDate, setLessonDate] = useState(() => searchParams.get("date") || todayLocal());
  // {slotId, date} stamp, or null. Seeded by the ?slot=&date= params or by
  // picking a "Scheduled today" occurrence — the occurrence's identity date is
  // what rides the request.
  const [stamp, setStamp] = useState(() => selectionFromParams(searchParams, "slot"));
  const [selectionError, setSelectionError] = useState(null);
  const [selectionChecking, setSelectionChecking] = useState(!!stamp?.appointmentId);
  const [saving, setSaving] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);

  useEffect(() => {
    if (!studentId && !classId) {
      navigate("/students", { replace: true });
      return undefined;
    }
    const controller = new AbortController();
    const load = isClass
      ? Promise.all([
          api.get(`/classes/${classId}`, { signal: controller.signal }),
          // Names for the absent list. The class itself carries member IDS
          // only, and the roster is the one thing on this page an instructor
          // has to read rather than type. Asked of the CLASS rather than of
          // your own students, because a class taught with a colleague keeps
          // its roster under whoever the record is filed under — reading your
          // own list would hand the second instructor an empty register.
          api.get(`/classes/${classId}/members`, { signal: controller.signal }).catch(() => ({ data: { members: [] } })),
        ]).then(([classRes, membersRes]) => {
          setRoster(
            (membersRes.data?.members || [])
              .map((st) => ({ student_id: st.student_id, name: st.name || "Student" }))
              .sort((a, b) => a.name.localeCompare(b.name)),
          );
          return classRes;
        })
      : api.get(`/students/${studentId}`, { signal: controller.signal });
    load
      .then((res) => { if (isClass) setCls(res.data); else setStudent(res.data); })
      .catch(() => {
        if (controller.signal.aborted) return;
        toast.error(isClass ? "Failed to load class" : "Failed to load student");
        navigate("/students", { replace: true });
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [studentId, classId, isClass, navigate]);

  useEffect(() => {
    if (!SCHEDULING_ENABLED) return undefined;
    const controller = new AbortController();
    teachingToday({ signal: controller.signal })
      .then((data) => setScheduledToday(data?.occurrences || []))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!stamp?.appointmentId && !stamp?.connectionId && stamp?.revision === undefined) {
      setSelectionChecking(false); setSelectionError(null); return undefined;
    }
    const controller = new AbortController();
    setSelectionChecking(true); setSelectionError(null);
    verifySelection(stamp, { classId, studentId }, { signal: controller.signal, purpose: 'notes' })
      .then((fresh) => { if (!controller.signal.aborted) { setStamp(fresh); setLessonDate(fresh.lessonDate); } })
      .catch((error) => { if (!controller.signal.aborted) setSelectionError(getErrorMessage(error, "Refresh the selected appointment.")); })
      .finally(() => { if (!controller.signal.aborted) setSelectionChecking(false); });
    return () => controller.abort();
    // Metadata updates do not change the selected identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stamp?.connectionId, stamp?.appointmentId, stamp?.revision, classId, studentId]);

  // Only surface occurrences for this exact target so a pick can't stamp the
  // notes to another student's lesson.
  const relevantOccurrences = useMemo(
    () => (isClass
      ? scheduledToday.filter((o) => o.class_id === classId)
      : scheduledToday.filter((o) => o.student_id === studentId)),
    [scheduledToday, isClass, classId, studentId],
  );

  const targetName = isClass
    ? cls?.name || "the class"
    : [student?.first_name, student?.last_name].filter(Boolean).join(" ") || "the student";
  const isRevoked = !isClass && student?.consent_status === "revoked";

  /* An org-scheduled class (decision D7): the date is the session's and so is
     the name. Both are shown, neither is editable, and the server refuses a
     lesson that arrives without one of the org's sessions attached — the
     controls here are the courtesy, not the guard. */
  const orgScheduled = isClass && !!cls?.org_scheduled;
  const orgName = cls?.org_name || "The organization";
  const needsSession = orgScheduled && !stamp;

  /* What the org asked of the session these notes are for (O7): its note to
     the instructor and the plan it attached, read from the class's sessions
     and matched on the stamp. Nothing shown when there is nothing to show. */
  const [session, setSession] = useState(null);
  /* The class's other sessions and its substitute terms, for Request sub
     (docs/substitute-requests.md) — from the same read as `session`. */
  const [classSessions, setClassSessions] = useState([]);
  const [subPolicy, setSubPolicy] = useState(null);
  const [requestingSub, setRequestingSub] = useState(false);
  const coveredElsewhere = Boolean(session?.covered && !session.covered.mine);
  const [sessionsVersion, setSessionsVersion] = useState(0);
  // Whether typing here is still a plan. State rather than an inline check, so
  // a page left open walks over the five-minute line on its own — the button
  // must not still say "Save prep notes" at ten past the hour.
  const [preparing, setPreparing] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const stampSlotId = stamp?.slotId, stampDate = stamp?.date;
  useEffect(() => {
    // Opened from a meeting, the meeting is named outright; otherwise it is
    // matched on the stamp, which is only an org class's concern.
    if (!classId || (!occurrenceId && (!orgScheduled || !stampSlotId))) { setSession(null); return undefined; }
    const controller = new AbortController();
    api.get(`/classes/${classId}/sessions`, { signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        const rows = res.data?.sessions || [];
        setClassSessions(rows);
        setSubPolicy(res.data?.class?.substitutes || null);
        setSession(
          rows.find((r) => (occurrenceId ? r.occurrence_id === occurrenceId : r.slot_id === stampSlotId && r.date === stampDate)) || null,
        );
      })
      .catch(() => {});
    return () => controller.abort();
  }, [orgScheduled, stampSlotId, stampDate, classId, occurrenceId, sessionsVersion]);

  /* The plan, back in the field it was written in — once, so a refetch never
     overwrites what is being typed. Nobody writes their lesson twice. */
  useEffect(() => {
    if (!session || seeded) return;
    setSeeded(true);
    setPreparing(Boolean(occurrenceId) && isPrepWindow(session));
    if (session.my_prep?.body) setSummaryText(session.my_prep.body);
    if (session.my_prep?.title && !searchParams.get("title")) setTitleText(session.my_prep.title);
    // Seeding happens once per meeting; the params are read, not tracked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, seeded, occurrenceId]);

  /* Cross the line while the page is open. A slow tick rather than one timer,
     so a laptop that slept through the boundary wakes on the right side of it. */
  useEffect(() => {
    if (!preparing || !session) return undefined;
    const id = setInterval(() => { if (!isPrepWindow(session)) setPreparing(false); }, 20000);
    return () => clearInterval(id);
  }, [preparing, session]);

  const pickOccurrence = (occ) => {
    if (occ.recordable === false) { guardedNavigate("/settings"); return; }
    if (sameSelection(stamp, occ)) {
      if (!orgScheduled) setStamp(null); // toggle off — an org session stays attached
      return;
    }
    setStamp(selectionFromOccurrence(occ));
    setLessonDate(occ.lesson_date || occ.date);
    if (!titleText.trim()) setTitleText(`${weekdayName(occ.date)} lesson`);
  };

  // Nothing is persisted until Save, so any typed content is unsaved work.
  const isDirty =
    !!titleText.trim() || !!summaryText.trim() || !!instructorNotes.trim() || cleanList(practiceItems).length > 0;

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Back to where the meeting was picked: a page opened from a session belongs
  // to that class's term, not to its roster.
  const backTo = isClass
    ? occurrenceId ? `/classes/${classId}/sessions` : `/classes/${classId}`
    : `/students/${studentId}`;
  const guardedNavigate = useCallback((to) => {
    if (isDirty) setPendingNav(to);
    else navigate(to);
  }, [isDirty, navigate]);

  /** Save a plan. Not a lesson: no occurrence consumed, no evidence written,
      nothing metered, nothing sent. The text is simply here again when the
      session starts. */
  const handleSavePrep = async () => {
    if (!summaryText.trim() || saving) return;
    setSaving(true);
    try {
      await api.put(`/classes/${classId}/prep-notes/${encodeURIComponent(occurrenceId)}`, {
        title: orgScheduled ? undefined : titleText.trim() || undefined,
        body: summaryText.trim(),
      });
      toast.success("Prep notes saved.");
      navigate(backTo, { replace: true });
    } catch (err) {
      // The session started between opening this page and pressing save. The
      // work is still on screen and is now a write-up; switch, rather than
      // leave someone pressing a button that will never work.
      if (err?.response?.status === 409) setPreparing(false);
      toast.error(getErrorMessage(err, "Couldn't save the prep notes."));
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!summaryText.trim() || saving || selectionChecking || selectionError || needsSession) return;
    setSaving(true);
    const body = {
      // The server names an org session itself; nothing typed here would be kept.
      title: orgScheduled ? undefined : titleText.trim() || undefined,
      summary: summaryText.trim(),
      practice_items: cleanList(practiceItems),
      student_focus: cleanList(studentFocus),
      core_concepts: cleanList(coreConcepts),
      instructor_notes: instructorNotes.trim() || undefined,
      lesson_date: lessonDate || undefined,
    };
    if (isClass) {
      body.absent_student_ids = absentIds;
      body.excused_student_ids = excusedIds;
    }
    // Clears the plan this write-up grew out of, so no meeting holds both.
    if (isClass && occurrenceId) body.prep_occurrence_id = occurrenceId;
    try {
      const fresh = await verifySelection(stamp, { classId, studentId }, { purpose: 'notes' });
      Object.assign(body, selectionPayload(fresh));
      if (fresh?.appointmentId) body.lesson_date = fresh.lessonDate;
      const res = isClass
        ? await api.post(`/classes/${classId}/lessons/manual`, body)
        : await api.post("/lessons/manual", { student_id: studentId, ...body });
      toast.success("Lesson notes saved.");
      // Replace so Back doesn't return to an empty compose page whose content
      // has already been persisted (mirrors mobile's navigation.replace).
      if (res.data?.lesson_id) {
        navigate(`/lesson/${res.data.lesson_id}${isClass ? "?kind=class" : ""}`, { replace: true });
      } else {
        navigate(backTo, { replace: true });
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't save the lesson notes."));
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--accent-yellow-text)]" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[900px] px-6 pb-16 pt-8 md:px-12" data-testid="manual-lesson-compose">
      {/* Top bar — mirrors LessonSummary's edit-mode bar */}
      {/* One action, so the bar stays a single row at every width rather than
          stacking a full-bleed Back over a stranded Save (the summary page's
          bar stacks because it carries four actions). */}
      <div className="mb-8 flex items-center justify-between gap-3">
        <Button variant="bare" icon="chevronLeft" onPress={() => guardedNavigate(backTo)} data-testid="manual-lesson-back">
          {isClass ? "Class" : "Back"}
        </Button>
        {/* No separate Cancel: unlike LessonSummary's edit mode — where Cancel
            drops back to the reading view — leaving here IS the cancel, and
            Back already runs the discard guard. */}
        <div className="flex flex-wrap gap-2.5">
          {/* Cover for this session until it starts. Inside the org's notice
              period the dialog says who to contact instead (the server's
              `requestable` decides which); off, started, covered or already
              asked, and the button is simply not here. */}
          {preparing && subPolicy?.enabled && session && session.status !== "cancelled" && !session.covered && !session.substitute_request && (
            <Button variant="ghost" icon="users" onPress={() => setRequestingSub(true)} data-testid="compose-request-sub">
              Request sub
            </Button>
          )}
          {preparing && session?.substitute_request?.status === "pending" && !session?.covered && (
            <span className="self-center font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="compose-sub-requested">
              Sub requested
            </span>
          )}
          <Button
            icon={saving ? undefined : "check"}
            onPress={preparing ? handleSavePrep : handleSave}
            disabled={!summaryText.trim() || saving || coveredElsewhere || (!preparing && (selectionChecking || !!selectionError || needsSession))}
            data-testid="manual-lesson-submit"
          >
            {saving && <Loader2 className="mr-0.5 h-4 w-4 animate-spin" />}
            {saving ? "Saving…" : preparing ? "Save prep notes" : "Save notes"}
          </Button>
        </div>
      </div>

      {coveredElsewhere && (
        <p
          className="mb-6 rounded-[12px] border border-[var(--studio-divider)] bg-[var(--studio-overlay)] px-4 py-3 font-sans text-[14px] text-[var(--text-secondary)]"
          data-testid="compose-covered"
        >
          A substitute is covering this session ({session.covered.name || "to be confirmed"}), so its notes are theirs to write.
        </p>
      )}
      <ClassTeachingDetails classId={classId} />
      {subPolicy?.enabled && (
        <RequestSubDialog
          open={requestingSub}
          onOpenChange={setRequestingSub}
          classId={classId}
          className={cls?.name}
          sessions={classSessions}
          policy={subPolicy}
          initialOccurrenceId={occurrenceId}
          onRequested={() => setSessionsVersion((v) => v + 1)}
        />
      )}
      <AlertDialog open={!!pendingNav} onOpenChange={(open) => { if (!open) setPendingNav(null); }}>
        <AlertDialogContent className="studio mx-4 max-w-[calc(100%-2rem)] border-[var(--studio-divider)] bg-[var(--studio-overlay)] text-[var(--text-primary)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard these notes?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-tertiary)]">
              This lesson hasn’t been saved yet. Leaving now will discard what you’ve typed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[var(--studio-divider)] bg-[var(--studio-interactive)] text-[var(--text-primary)]">
              Keep writing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const to = pendingNav; setPendingNav(null); navigate(to); }}
              className="bg-[var(--state-danger)] text-[var(--text-on-state)]"
              data-testid="discard-manual-lesson-confirm"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Two-row grid: the title shares row 1 with the "When" card, the reading
          column shares row 2 with the rest of the rail — same shape as the
          lesson document so the two surfaces read as one. */}
      <div className="grid grid-cols-1 items-start gap-x-10 gap-y-9 lg:grid-cols-[1fr_300px]">
        {/* Title + meta — row 1, left */}
        <div className="lg:col-start-1 lg:row-start-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {isClass && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-2.5 py-1 text-xs font-medium text-[var(--accent-yellow-text)]" data-testid="class-lesson-badge">
                <Icon name="users" size={13} /> Class lesson
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)]" data-testid="typed-notes-badge">
              <Icon name="edit" size={13} /> {preparing ? "Prep notes" : "Typed notes"}
            </span>
          </div>
          {orgScheduled ? (
            <h1
              className="mt-3 font-serif text-[32px] font-normal leading-[1.15] tracking-[-0.02em] text-[var(--text-primary)]"
              data-testid="manual-lesson-title-locked"
            >
              {cls?.name}
            </h1>
          ) : (
            <input
              type="text"
              value={titleText}
              maxLength={140}
              onChange={(e) => setTitleText(e.target.value)}
              placeholder="Lesson title (optional)"
              aria-label="Lesson title"
              data-testid="manual-lesson-title"
              className={`${FIELD_CLS} mt-3 font-serif text-[32px] leading-[1.15] tracking-[-0.02em]`}
            />
          )}
          <div className="mt-3.5 flex flex-wrap items-center gap-3 font-mono text-[13px] text-[var(--text-tertiary)]">
            <span data-testid="manual-lesson-target">{targetName}</span>
            <span className="opacity-40">·</span>
            <span>{formatDate(lessonDate) || "No date"}</span>
            <span className="opacity-40">·</span>
            <span>No recording</span>
          </div>
          {SCHEDULER_VIEW_ENABLED && <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="manual-business-context">
            {selectionChecking ? "Checking appointment…" : stamp?.contextLabel || "Unassigned"}
          </p>}
          {selectionError && <p role="alert" className="mt-3 text-sm text-[var(--state-warning)]">{selectionError}</p>}
          {isClass && (
            <p className="mt-2 font-sans text-xs text-[var(--text-tertiary)]" data-testid="class-edit-note">
              {preparing
                ? "This class hasn’t started. Saving keeps a plan — nothing is recorded as taught and nothing goes to families. It’ll be waiting in this field when the session begins."
                : "One shared record — edits and sends apply to this lesson’s participants."}
            </p>
          )}
          {needsSession && !preparing && (
            <p role="alert" className="mt-3 font-sans text-sm text-[var(--state-warning)]" data-testid="manual-lesson-needs-session">
              Open this from the class page and choose the session these notes are for — {orgName} sets this
              class&rsquo;s schedule.
            </p>
          )}
          <SessionBrief session={session} className="mt-3" />
        </div>

        {/* Reading column — row 2, left */}
        <div className="min-w-0 lg:col-start-1 lg:row-start-2">
          <Eyebrow>{preparing ? "Your plan" : sectionLabel("summary", "instructor")}</Eyebrow>
          <div className="mt-3">
            <RichTextEditor
              value={summaryText}
              onChange={setSummaryText}
              placeholder={preparing ? "What you plan to cover…" : "What you covered, how it went…"}
              ariaLabel="Lesson summary"
              testId="manual-lesson-summary"
              minHeight={220}
              className="font-serif text-[17px] leading-[1.6]"
            />
          </div>

          <div className="mt-10">
            <Eyebrow>{sectionLabel("student_focus", "instructor")}</Eyebrow>
            <div className="mt-4">
              <EditableList
                {...listProps(studentFocus, setStudentFocus, "manual-focus", {
                  numbered: true,
                  placeholder: isClass ? "What the class is working toward" : "What they are working toward",
                  addLabel: "Add focus",
                })}
              />
            </div>
          </div>

          <div className="mt-10">
            <Eyebrow>{sectionLabel("practice_items", "instructor")}</Eyebrow>
            <div className="mt-4">
              <EditableList
                {...listProps(practiceItems, setPracticeItems, "manual-practice", {
                  numbered: true,
                  placeholder: "Something to practice",
                  addLabel: "Add practice item",
                })}
              />
            </div>
          </div>
        </div>

        {/* When — row 1, right (in line with the title); ordered first on mobile
            so the date and the scheduled pick sit under the Save button. */}
        <div className="order-first lg:order-none lg:col-start-2 lg:row-start-1" data-testid="manual-lesson-when">
          <LessonWorkspaceStatus connectionId={stamp?.connectionId} appointmentId={stamp?.appointmentId}
            onRevision={revision => setStamp(previous => ({ ...previous, revision }))} />
          <Eyebrow>When</Eyebrow>
          <div className="mt-3 flex flex-col gap-3">
            <input
              type="date"
              value={lessonDate}
              onChange={(e) => { setLessonDate(e.target.value); setStamp(null); }}
              // A plan is written FOR one meeting: there is no other day to
              // move it to.
              disabled={orgScheduled || preparing}
              aria-label="Lesson date"
              data-testid="manual-lesson-date"
              className={`${FIELD_CLS} font-mono text-[13px] disabled:opacity-70`}
            />
            {orgScheduled && (
              <p
                className="inline-flex items-center gap-1.5 font-sans text-[12px] text-[var(--text-tertiary)]"
                data-testid="manual-lesson-org-schedule"
              >
                <Icon name="lock" size={13} />
                {orgName} sets this schedule and names its sessions.
              </p>
            )}
            {!preparing && relevantOccurrences.length > 0 && (
              <div data-testid="manual-scheduled-today">
                <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">Scheduled today</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {relevantOccurrences.map((occ) => {
                    const picked = sameSelection(stamp, occ);
                    return (
                      <button
                        type="button"
                        key={occurrenceKey(occ)}
                        onClick={() => pickOccurrence(occ)}
                        data-testid={`manual-scheduled-pick-${occ.scheduler_appointment_id || occ.slot_id}`}
                        className={[
                          "rounded-full border px-3 py-1.5 font-sans text-xs transition-colors",
                          picked
                            ? "border-[var(--accent-yellow-border-strong)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
                            : "border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] hover:border-[var(--accent-yellow-border)]",
                        ].join(" ")}
                      >
                        {occ.display_name || "Lesson"} · {formatTime12h(occ.time_local)}
                        {SCHEDULER_VIEW_ENABLED && ` · ${occ.context_label || "Unassigned"}`}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 font-sans text-[11.5px] leading-relaxed text-[var(--text-quaternary)]">
                  Linking the lesson to a scheduled time keeps it in your ledger and billing.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Rail — row 2, right */}
        <div className="flex flex-col gap-6 lg:col-start-2 lg:row-start-2 lg:sticky lg:top-8">
          {/* Who wasn't there. A list of names you tap, not a row of dropdowns:
              taking a register is a scan down a class list, and an instructor
              doing it between two lessons should be able to do it with a thumb.
              Absent and excused are the only states worth a tap — "present" is
              what everyone else is, and asking for it once per student would
              turn a ten-second job into a form. A tap cycles a name through
              absent, excused and back to unmarked, so the common case (absent)
              is still one tap.

              Nothing marked is NOT a claim that everyone attended; it is an
              instructor who didn't take a register, and nothing downstream may
              read it as attendance. */}
          {/* No register before the class: who missed a session is a fact
              about a session that has happened, and asking in advance invites
              a guess into the one field downstream treats as observed. */}
          {isClass && !preparing && roster.length > 0 && (
            <Card padding={16} data-testid="manual-lesson-absences">
              <div className="flex items-baseline justify-between gap-3">
                <Eyebrow>Absent or excused</Eyebrow>
                {marks.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setMarks(new Map())}
                    data-testid="manual-absent-clear"
                    className="font-sans text-[12px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="mt-1.5 font-sans text-[12px] leading-relaxed text-[var(--text-quaternary)]">
                Tap anyone who missed this session; tap again for excused. Everyone else is left as they are.
              </p>
              {/* Scrollable rather than paged: a long roster still scans in one
                  gesture, and the card keeps its place in the rail. */}
              <div className="mt-3 flex max-h-[280px] flex-col gap-1 overflow-y-auto pr-1">
                {roster.map((st) => {
                  const mark = marks.get(st.student_id);
                  return (
                    <button
                      type="button"
                      key={st.student_id}
                      aria-pressed={!!mark}
                      aria-label={mark ? `${st.name}, ${mark}` : st.name}
                      onClick={() =>
                        setMarks((prev) => {
                          const next = new Map(prev);
                          const now = next.get(st.student_id);
                          if (!now) next.set(st.student_id, "absent");
                          else if (now === "absent") next.set(st.student_id, "excused");
                          else next.delete(st.student_id);
                          return next;
                        })
                      }
                      data-testid={`manual-absent-${st.student_id}`}
                      data-mark={mark || "none"}
                      className={[
                        "flex items-center justify-between gap-2 rounded-[10px] px-2.5 py-1.5 text-left font-sans text-[14px] transition-colors",
                        mark === "absent"
                          ? "bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
                          : mark === "excused"
                            ? "bg-[var(--studio-interactive)] text-[var(--text-primary)]"
                            : "text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)]",
                      ].join(" ")}
                    >
                      <span className="truncate">{st.name}</span>
                      {mark && (
                        <span className="flex shrink-0 items-center gap-1 font-sans text-[12px]">
                          {mark === "absent" ? "Absent" : "Excused"}
                          {mark === "absent" && <Icon name="x" size={15} />}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 font-sans text-[12px] text-[var(--text-tertiary)]" data-testid="manual-absent-count">
                {marks.size === 0
                  ? "Nobody marked absent"
                  : [
                      absentIds.length ? `${absentIds.length} absent` : null,
                      excusedIds.length ? `${excusedIds.length} excused` : null,
                    ]
                      .filter(Boolean)
                      .join(", ") + ` of ${roster.length}`}
              </p>
            </Card>
          )}

          {isRevoked && (
            <div
              className="rounded-[16px] border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] p-4 font-sans text-[13px] leading-relaxed text-[var(--accent-yellow-text)]"
              data-testid="manual-lesson-revoked-note"
            >
              Recording stays locked for this student; notes save privately and no email goes out.
            </div>
          )}

          <Card padding={16}>
            <Eyebrow>{sectionLabel("instructor_notes", "instructor")}</Eyebrow>
            <div className="mt-3">
              <RichTextEditor
                value={instructorNotes}
                onChange={setInstructorNotes}
                placeholder={isClass ? "A line from you to the families. They read it as Notes from your teacher." : "A line from you to the family. They read it as Notes from your teacher."}
                ariaLabel="Instructor notes"
                testId="manual-lesson-notes"
                minHeight={100}
                className="text-[15px]"
              />
            </div>
          </Card>

          <Card padding={16}>
            <Eyebrow>{sectionLabel("core_concepts", "instructor")}</Eyebrow>
            <div className="mt-3">
              <EditableList
                {...listProps(coreConcepts, setCoreConcepts, "manual-concepts", {
                  placeholder: "A concept the lesson touched",
                  addLabel: "Add concept",
                })}
              />
            </div>
          </Card>

          <p className="font-sans text-[12px] leading-relaxed text-[var(--text-quaternary)]">
            {preparing ? (
              <>
                A plan is not a lesson — nothing counts it as taught and no family is sent it.{" "}
                {orgName} can see it on their class page, beside anything they&rsquo;ve
                asked for. Five minutes before {formatDate(lessonDate)} it becomes this session&rsquo;s notes, with
                everything you&rsquo;ve written already in place.
              </>
            ) : (
              <>
                Typed notes join the lesson history like any lesson — you can edit them, export a PDF, or
                email them{isClass ? " to its participants" : " to the student"} afterwards.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ManualLessonCompose;
